import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext'; // Path adjust karo
import BlogEditor from './BlogEditor';
import BlogSeoFields from './BlogSeoFields';
import '../styles/admin-upload-blog.css';
import { API_URL } from '../../confi';
const API_BASE = `${API_URL}/api`; 

const slugify = (text) =>
  text
    ?.toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]+/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '') || '';

const BlogForm = () => {
  const { token, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    author_name: '',
    image: null,
    // SEO Settings
    title_tag: '',
    meta_description: '',
    keywords: '',
    url_title: '',
    canonical_url: '',
    focus_keyword: '',
    og_title: '',
    og_description: '',
    meta_robots: 'index, follow',
  });
  const [imagePreview, setImagePreview] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // Initial auth check - sirf page load pe check, agar nahi logged in to redirect
  useEffect(() => {
    if (!isAuthenticated || !token) {
      toast.error('Please login first.');
      navigate('/login'); // Sirf yaha navigate, handleSubmit me nahi
    } else {
      // console.log('BlogForm loaded with token:', token.substring(0, 20) + '...');
    }
  }, [isAuthenticated, token, navigate]);

  // Drag and drop handlers (same as before)
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!dropAreaRef.current?.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB!');
        return;
      }
      setFormData((prev) => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      toast.error('Please select a valid image file (JPG, PNG, WEBP).');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Auto-generate url_title if user hasn't explicitly customized it
      if (name === 'title' && (!prev.url_title || prev.url_title === slugify(prev.title))) {
        updated.url_title = slugify(value);
      }
      return updated;
    });
  };

  const handleSlugGenerate = () => {
    if (formData.title) {
      setFormData((prev) => ({
        ...prev,
        url_title: slugify(prev.title),
      }));
      toast.info('URL slug generated from title!');
    } else {
      toast.warn('Please enter a blog title first.');
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleEditorChange = (value) => {
    setFormData((prev) => ({ ...prev, content: value }));
  };

  const handleReset = () => {
    setFormData({
      title: '',
      content: '',
      author_name: '',
      image: null,
      title_tag: '',
      meta_description: '',
      keywords: '',
      url_title: '',
      canonical_url: '',
      focus_keyword: '',
      og_title: '',
      og_description: '',
      meta_robots: 'index, follow',
    });
    setImagePreview('');
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setResponseMessage('');
    setIsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log('Submitting form with data:', formData);
    console.log('Using token from context:', token ? token.substring(0, 20) + '...' : 'No token');

    // No need for extra check here - useEffect already handled
    const { title, content, author_name, image } = formData;

    if (!title || !content || !author_name || !image) {
      toast.error('Please fill in all required fields (Title, Category, Image, Content)!');
      setResponseMessage('Please fill in all required fields!');
      setIsError(true);
      setLoading(false);
      return;
    }

    try {
      const data = new FormData();
      data.append('title', title);
      data.append('content', content);
      data.append('author_name', author_name);
      data.append('image', image);

      // Append SEO fields
      const finalSlug = formData.url_title || slugify(title);
      data.append('title_tag', formData.title_tag || title);
      data.append('meta_title', formData.title_tag || title);
      data.append('meta_description', formData.meta_description || '');
      data.append('keywords', formData.keywords || '');
      data.append('meta_keywords', formData.keywords || '');
      data.append('url_title', finalSlug);
      data.append('slug', finalSlug);
      data.append('canonical_url', formData.canonical_url || '');
      data.append('focus_keyword', formData.focus_keyword || '');
      data.append('og_title', formData.og_title || formData.title_tag || title);
      data.append('og_description', formData.og_description || formData.meta_description || '');
      data.append('twitter_title', formData.og_title || formData.title_tag || title);
      data.append('twitter_description', formData.og_description || formData.meta_description || '');
      data.append('meta_robots', formData.meta_robots || 'index, follow');

      const response = await fetch(`${API_BASE}/addblog`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: data,
      });

      const text = await response.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        toast.error('Server returned invalid response.');
        setIsError(true);
        setLoading(false);
        return;
      }

      if (response.ok) {
        toast.success(result.message || 'Blog uploaded successfully!');
        setResponseMessage('Blog uploaded successfully!');
        setIsError(false);
        handleReset();
      } else {
        let errorMessage = result.message || `Error ${response.status}`;
        if (response.status === 401) {
          errorMessage = 'Token expired. Logging out...';
          logout(); // Clears token, useEffect will redirect
        } else if (response.status === 403) {
          errorMessage = 'No permission to upload.';
        }
        toast.error(errorMessage);
        setResponseMessage(errorMessage);
        setIsError(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to upload blog.';
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        logout(); // Clears token on network 401
        errorMessage = 'Token invalid. Logging out...';
      }
      toast.error(errorMessage);
      setResponseMessage(errorMessage);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-upload-blog">
      <div className="admin-upload-header">
        <div className="header-gradient-bg">
          <h1 className="admin-upload-title">Create New Blog Post</h1>
          <p className="admin-upload-subtitle">Share your thoughts and ideas with the world</p>
          <div className="header-decoration">
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
          </div>
        </div>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="admin-upload-form">
          <div className="form-card">
            <div className="form-group-enhanced">
              <label htmlFor="title" className="form-label-enhanced">
                <span className="label-icon">📝</span>
                Blog Title
                <span className="required-star">*</span>
              </label>
              <div className="input-container">
                <input
                  type="text"
                  id="title"
                  name="title"
                  className="form-input-enhanced"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Craft an engaging title for your blog..."
                />
                <div className="input-focus-border"></div>
              </div>
            </div>
          </div>

          <div className="form-row-responsive">
            <div className="form-card">
              <div className="form-group-enhanced">
                <label htmlFor="author_name" className="form-label-enhanced">
                  <span className="label-icon">👤</span>
                 Category
                  <span className="required-star">*</span>
                </label>
                <div className="input-container">
                 <select
                      id="author_name"
                      name="author_name"
                      className="form-input-enhanced"
                      value={formData.author_name}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Category</option>
                      <option value="Housekeeping">Housekeeping</option>
                      <option value="Baby Sitting">Baby Sitting</option>
                      <option value="Elderly Care">Elderly Care</option>
                      <option value="Patient Care">Patient Care</option>
                      <option value="Cook">Cook</option>
                      <option value="Driver">Driver</option>
                    </select>
                  <div className="input-focus-border"></div>
                </div>
              </div>
            </div>

            <div className="form-card">
              <div className="form-group-enhanced">
                <label className="form-label-enhanced">
                  <span className="label-icon">🖼️</span>
                  Blog Image
                  <span className="required-star">*</span>
                </label>
                <div
                  ref={dropAreaRef}
                  className={`image-upload-area ${isDragging ? 'drag-over' : ''} ${imagePreview ? 'has-image' : ''}`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {imagePreview ? (
                    <div className="image-preview-container">
                      <img src={imagePreview} alt="Preview" className="uploaded-image" />
                      <div className="image-overlay">
                        <span className="change-image-text">Click to change image</span>
                      </div>
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <div className="upload-icon">📁</div>
                      <div className="upload-text">
                        <p className="upload-main-text">Drop your image here or click to browse</p>
                        <p className="upload-sub-text">Supports JPG, PNG, WEBP - Max 5MB</p>
                      </div>
                    </div>
                  )}
                  <input
                    type="file"
                    id="image"
                    name="image"
                    className="hidden-file-input"
                    accept="image/*"
                    onChange={handleImageChange}
                    required={!formData.image}
                    ref={fileInputRef}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-card">
            <div className="form-group-enhanced">
              <label htmlFor="content" className="form-label-enhanced">
                <span className="label-icon">📄</span>
                Blog Content
                <span className="required-star">*</span>
              </label>
              <div className="editor-container">
                <BlogEditor
                  id="content"
                  name="content"
                  className="blog-editor-enhanced"
                  value={formData.content}
                  onChange={handleEditorChange}
                  placeholder="Write your amazing blog content here..."
                />
              </div>
            </div>
          </div>

          {/* SEO & Meta Settings */}
          <BlogSeoFields
            formData={formData}
            imagePreview={imagePreview}
            onChange={handleInputChange}
            onSlugGenerate={handleSlugGenerate}
            mode="page"
          />

          <div className="form-actions-enhanced">
            <button
              type="button"
              onClick={handleReset}
              className="btn-secondary-enhanced"
              disabled={loading}
            >
              <span className="btn-icon">🔄</span>
              Reset Form
            </button>
            <button
              type="submit"
              className="btn-primary-enhanced"
              disabled={loading}
            >
              <span className="btn-icon">
                {loading ? '⏳' : '🚀'}
              </span>
              {loading ? 'Publishing...' : 'Publish Blog'}
            </button>
          </div>
        </form>

        {responseMessage && (
          <div className={`response-message ${isError ? 'error' : 'success'}`}>
            <div className="message-content">
              <span className="message-icon">
                {isError ? '❌' : '✅'}
              </span>
              {responseMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogForm;